import React from 'react';
import { SaiPersistenceDimension, SaiWellbeingDimension } from '../../../../graphQL';

/**
 * One icon per index dimension, for the score-contribution badges. The paths are lifted from the
 * product mocks, each of which was a full pill composition: only the stroked icon path is kept,
 * with the viewBox cropped to that icon's region of the original canvas so the data stays verbatim.
 */

type IconDefinition = { viewBox: string; paths: React.ReactNode };

const strokeProps = {
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  fill: 'none',
} as const;

const WELLBEING_ICONS: Record<SaiWellbeingDimension, IconDefinition> = {
  physical: {
    viewBox: '42 24 18 18',
    paths: (
      <>
        <path
          {...strokeProps}
          d="M45.6 25.725C45.6027 25.7722 45.6201 25.8173 45.6498 25.8539C45.6795 25.8906 45.72 25.917 45.7656 25.9294C45.8111 25.9418 45.8594 25.9396 45.9036 25.923C45.9478 25.9064 45.9857 25.8764 46.0119 25.8371C46.038 25.7978 46.0512 25.7513 46.0495 25.7042C46.0478 25.657 46.0313 25.6115 46.0023 25.5743C45.9734 25.537 45.9334 25.5098 45.8881 25.4965C45.8429 25.4831 45.7945 25.4844 45.75 25.5H45C44.6022 25.5 44.2206 25.6581 43.9393 25.9394C43.658 26.2207 43.5 26.6022 43.5 27V30.75C43.5 31.9435 43.9741 33.0881 44.818 33.932C45.6619 34.7759 46.8065 35.25 48 35.25C49.1935 35.25 50.3381 34.7759 51.182 33.932C52.0259 33.0881 52.5 31.9435 52.5 30.75V27C52.5 26.6022 52.342 26.2207 52.0607 25.9394C51.7794 25.6581 51.3978 25.5 51 25.5H50.25C50.2352 25.5148 50.2235 25.5324 50.2155 25.5517C50.2075 25.571 50.2034 25.5917 50.2034 25.6125C50.2034 25.6334 50.2075 25.6541 50.2155 25.6734C50.2235 25.6927 50.2352 25.7103 50.25 25.725C50.2648 25.7398 50.2823 25.7515 50.3016 25.7595C50.3209 25.7675 50.3416 25.7716 50.3625 25.7716C50.3834 25.7716 50.4041 25.7675 50.4234 25.7595C50.4427 25.7515 50.4602 25.7398 50.475 25.725"
        />
        <path
          {...strokeProps}
          d="M48 35.25V36C48 37.1935 48.4741 38.3381 49.318 39.182C50.1619 40.0259 51.3065 40.5 52.5 40.5V40.5C53.6935 40.5 54.8381 40.0259 55.682 39.182C56.5259 38.3381 57 37.1935 57 36V33"
        />
        <path
          {...strokeProps}
          d="M57 33C57.8284 33 58.5 32.3284 58.5 31.5C58.5 30.6716 57.8284 30 57 30C56.1716 30 55.5 30.6716 55.5 31.5C55.5 32.3284 56.1716 33 57 33Z"
        />
      </>
    ),
  },
  intellectual: {
    viewBox: '37.75 24.75 10.5 16.5',
    paths: (
      <>
        <path {...strokeProps} d="M40.75 37.5H45.25" />
        <path {...strokeProps} d="M41.5 40.5H44.5" />
        <path
          {...strokeProps}
          d="M45.3175 34.5C45.4525 33.765 45.805 33.195 46.375 32.625C46.7371 32.2916 47.0246 31.8854 47.2185 31.4329C47.4124 30.9805 47.5083 30.4922 47.5 30C47.5 28.8065 47.0259 27.6619 46.182 26.818C45.3381 25.9741 44.1935 25.5 43 25.5C41.8065 25.5 40.6619 25.9741 39.818 26.818C38.9741 27.6619 38.5 28.8065 38.5 30C38.5 30.75 38.6725 31.6725 39.625 32.625C40.168 33.1215 40.5385 33.7784 40.6825 34.5"
        />
      </>
    ),
  },
  social: {
    viewBox: '47.5 23.75 15 16.5',
    paths: (
      <>
        <path
          {...strokeProps}
          d="M59.5 29C60.7426 29 61.75 27.9926 61.75 26.75C61.75 25.5074 60.7426 24.5 59.5 24.5C58.2574 24.5 57.25 25.5074 57.25 26.75C57.25 27.9926 58.2574 29 59.5 29Z"
        />
        <path
          {...strokeProps}
          d="M50.5 34.25C51.7426 34.25 52.75 33.2426 52.75 32C52.75 30.7574 51.7426 29.75 50.5 29.75C49.2574 29.75 48.25 30.7574 48.25 32C48.25 33.2426 49.2574 34.25 50.5 34.25Z"
        />
        <path
          {...strokeProps}
          d="M59.5 39.5C60.7426 39.5 61.75 38.4926 61.75 37.25C61.75 36.0074 60.7426 35 59.5 35C58.2574 35 57.25 36.0074 57.25 37.25C57.25 38.4926 58.2574 39.5 59.5 39.5Z"
        />
        <path {...strokeProps} d="M52.4424 33.1323L57.5649 36.1173" />
        <path {...strokeProps} d="M57.5574 27.8823L52.4424 30.8673" />
      </>
    ),
  },
  emotional: {
    viewBox: '39.5 25.5 15 15',
    paths: (
      <>
        <path
          {...strokeProps}
          d="M47 29.625C47 28.9575 47.1979 28.305 47.5688 27.75C47.9396 27.1949 48.4667 26.7624 49.0834 26.5069C49.7001 26.2515 50.3787 26.1846 51.0334 26.3149C51.6881 26.4451 52.2895 26.7665 52.7615 27.2385C53.2335 27.7105 53.5549 28.3119 53.6852 28.9666C53.8154 29.6213 53.7485 30.2999 53.4931 30.9166C53.2376 31.5333 52.8051 32.0604 52.2501 32.4312C51.695 32.8021 51.0425 33 50.375 33M47 29.625C47 28.9575 46.8021 28.305 46.4312 27.75C46.0604 27.1949 45.5333 26.7624 44.9166 26.5069C44.2999 26.2515 43.6213 26.1846 42.9666 26.3149C42.3119 26.4451 41.7105 26.7665 41.2385 27.2385C40.7665 27.7105 40.4451 28.3119 40.3149 28.9666C40.1846 29.6213 40.2515 30.2999 40.5069 30.9166C40.7624 31.5333 41.1949 32.0604 41.75 32.4312C42.305 32.8021 42.9575 33 43.625 33M47 29.625V30.75M43.625 33C42.9575 33 42.305 33.1979 41.75 33.5688C41.1949 33.9396 40.7624 34.4667 40.5069 35.0834C40.2515 35.7001 40.1846 36.3787 40.3149 37.0334C40.4451 37.6881 40.7665 38.2895 41.2385 38.7615C41.7105 39.2335 42.3119 39.5549 42.9666 39.6852C43.6213 39.8154 44.2999 39.7485 44.9166 39.4931C45.5333 39.2376 46.0604 38.8051 46.4312 38.2501C46.8021 37.695 47 37.0425 47 36.375M43.625 33H44.75M50.375 33C51.0425 33 51.695 33.1979 52.2501 33.5688C52.8051 33.9396 53.2376 34.4667 53.4931 35.0834C53.7485 35.7001 53.8154 36.3787 53.6852 37.0334C53.5549 37.6881 53.2335 38.2895 52.7615 38.7615C52.2895 39.2335 51.6881 39.5549 51.0334 39.6852C50.3787 39.8154 49.7001 39.7485 49.0834 39.4931C48.4667 39.2376 47.9396 38.8051 47.5688 38.2501C47.1979 37.695 47 37.0425 47 36.375M50.375 33H49.25M47 36.375V35.25"
        />
        <path
          {...strokeProps}
          d="M47 35.25C48.2426 35.25 49.25 34.2426 49.25 33C49.25 31.7574 48.2426 30.75 47 30.75C45.7574 30.75 44.75 31.7574 44.75 33C44.75 34.2426 45.7574 35.25 47 35.25Z"
        />
        <path {...strokeProps} d="M44 36L45.125 34.875" />
        <path {...strokeProps} d="M48.875 31.125L50 30" />
        <path {...strokeProps} d="M44 30L45.125 31.125" />
        <path {...strokeProps} d="M48.875 34.875L50 36" />
      </>
    ),
  },
  financial: {
    viewBox: '40.75 25 16.5 12.75',
    paths: (
      <>
        <path
          {...strokeProps}
          d="M54.25 25.75C53.125 25.75 52.15 26.8 52 27.25C49.375 26.125 43.75 27.025 43.75 31C43.75 32.35 43.75 33.25 45.25 34.375V37H48.25V35.5H50.5V37H53.5V34C54.25 33.625 54.775 33.25 55 32.5H56.5V29.5H55C55 28.75 54.625 28.375 54.25 28V25.75Z"
        />
        <path {...strokeProps} d="M41.5 28.75V29.5C41.5 30.325 42.175 31 43 31H43.75" />
      </>
    ),
  },
  occupational: {
    viewBox: '33.25 23.5 14.25 14.25',
    paths: (
      <path
        {...strokeProps}
        d="M41.7999 27.2001C41.6778 27.3248 41.6093 27.4923 41.6093 27.6668C41.6093 27.8413 41.6778 28.0089 41.7999 28.1335L42.8666 29.2001C42.9912 29.3223 43.1587 29.3907 43.3332 29.3907C43.5077 29.3907 43.6753 29.3223 43.7999 29.2001L46.3132 26.6868C46.6485 27.4276 46.75 28.253 46.6042 29.0529C46.4585 29.8529 46.0724 30.5894 45.4974 31.1643C44.9225 31.7393 44.1859 32.1254 43.386 32.2711C42.5861 32.4169 41.7607 32.3154 41.0199 31.9801L36.4132 36.5868C36.148 36.852 35.7883 37.001 35.4132 37.001C35.0382 37.001 34.6785 36.852 34.4132 36.5868C34.148 36.3216 33.999 35.9619 33.999 35.5868C33.999 35.2117 34.148 34.852 34.4132 34.5868L39.0199 29.9801C38.6847 29.2394 38.5832 28.414 38.7289 27.6141C38.8747 26.8141 39.2608 26.0776 39.8357 25.5026C40.4107 24.9277 41.1472 24.5416 41.9471 24.3958C42.7471 24.2501 43.5724 24.3516 44.3132 24.6868L41.8066 27.1935L41.7999 27.2001Z"
      />
    ),
  },
  environmental: {
    viewBox: '28.25 24.5 13.5 15',
    paths: (
      <>
        <path
          {...strokeProps}
          d="M29 30.0002L35 25.3335L41 30.0002V37.3335C41 37.6871 40.8595 38.0263 40.6095 38.2763C40.3594 38.5264 40.0203 38.6668 39.6667 38.6668H30.3333C29.9797 38.6668 29.6406 38.5264 29.3905 38.2763C29.1405 38.0263 29 37.6871 29 37.3335V30.0002Z"
        />
        <path {...strokeProps} d="M33 38.6667V32H37V38.6667" />
      </>
    ),
  },
  spiritual: {
    viewBox: '45.25 26.25 12.75 13.5',
    paths: (
      <>
        <path {...strokeProps} d="M48.25 39H55.75" />
        <path {...strokeProps} d="M50.5 39C54.625 37.125 51.1 34.2 52.75 31.5" />
        <path
          {...strokeProps}
          d="M50.125 31.0499C50.95 31.6499 51.475 32.6999 51.85 33.8249C50.35 34.1249 49.225 34.1249 48.25 33.5999C47.35 33.1499 46.525 32.1749 46 30.4499C48.1 30.0749 49.3 30.4499 50.125 31.0499V31.0499Z"
        />
        <path
          {...strokeProps}
          d="M53.5751 28.5C53.0033 29.3937 52.7156 30.4396 52.7501 31.5C54.1751 31.425 55.2251 31.05 55.9751 30.45C56.7251 29.7 57.1751 28.725 57.2501 27C55.2251 27.075 54.2501 27.75 53.5751 28.5Z"
        />
      </>
    ),
  },
};

const PERSISTENCE_ICONS: Record<SaiPersistenceDimension, IconDefinition> = {
  lifeStability: {
    viewBox: '37 23 16 16',
    paths: (
      <>
        <path
          {...strokeProps}
          d="M44.9999 37.6668C48.6818 37.6668 51.6666 34.6821 51.6666 31.0002C51.6666 27.3183 48.6818 24.3335 44.9999 24.3335C41.318 24.3335 38.3333 27.3183 38.3333 31.0002C38.3333 34.6821 41.318 37.6668 44.9999 37.6668Z"
        />
        <path
          {...strokeProps}
          d="M44.9999 33.6668C46.4727 33.6668 47.6666 32.4729 47.6666 31.0002C47.6666 29.5274 46.4727 28.3335 44.9999 28.3335C43.5272 28.3335 42.3333 29.5274 42.3333 31.0002C42.3333 32.4729 43.5272 33.6668 44.9999 33.6668Z"
        />
        <path {...strokeProps} d="M40.2866 26.2866L43.1133 29.1133" />
        <path {...strokeProps} d="M46.8867 32.8867L49.7134 35.7134" />
        <path {...strokeProps} d="M46.8867 29.1133L49.7134 26.2866" />
        <path {...strokeProps} d="M46.8867 29.1131L49.2401 26.7598" />
        <path {...strokeProps} d="M40.2866 35.7134L43.1133 32.8867" />
      </>
    ),
  },
  campusBelonging: {
    viewBox: '19.25 23.5 17.5 15',
    paths: (
      <>
        <path
          {...strokeProps}
          d="M34.3151 25.4348C33.9388 25.0573 33.4917 24.7577 32.9994 24.5533C32.5071 24.3489 31.9794 24.2437 31.4463 24.2437C30.9133 24.2437 30.3855 24.3489 29.8932 24.5533C29.4009 24.7577 28.9538 25.0573 28.5776 25.4348L28.0001 26.0198L27.4226 25.4348C27.0463 25.0573 26.5992 24.7577 26.1069 24.5533C25.6146 24.3489 25.0869 24.2437 24.5538 24.2437C24.0208 24.2437 23.493 24.3489 23.0007 24.5533C22.5084 24.7577 22.0613 25.0573 21.6851 25.4348C20.0951 27.0248 19.9976 29.7098 22.0001 31.7498L28.0001 37.7498L34.0001 31.7498C36.0026 29.7098 35.9051 27.0248 34.3151 25.4348Z"
        />
        <path
          {...strokeProps}
          d="M28 26.02L25.6525 28.375C25.3557 28.6742 25.1892 29.0786 25.1892 29.5C25.1892 29.9215 25.3557 30.3258 25.6525 30.625V30.625C25.9517 30.9218 26.3561 31.0883 26.7775 31.0883C27.199 31.0883 27.6033 30.9218 27.9025 30.625L29.5975 28.9675C30.0188 28.5507 30.5874 28.317 31.18 28.317C31.7726 28.317 32.3413 28.5507 32.7625 28.9675L34.5625 30.7675"
        />
        <path {...strokeProps} d="M32.5 33.25L31 31.75" />
        <path {...strokeProps} d="M30.25 35.5L28.75 34" />
      </>
    ),
  },
  personalWellness: {
    viewBox: '19 23 16 16',
    paths: (
      <path
        {...strokeProps}
        d="M26.3333 24.3335C25.9796 24.3335 25.6405 24.474 25.3904 24.724C25.1404 24.9741 24.9999 25.3132 24.9999 25.6668V29.0002H21.6666C21.313 29.0002 20.9738 29.1406 20.7238 29.3907C20.4737 29.6407 20.3333 29.9799 20.3333 30.3335V31.6668C20.3333 32.4002 20.9333 33.0002 21.6666 33.0002H24.9999V36.3335C24.9999 37.0668 25.5999 37.6668 26.3333 37.6668H27.6666C28.0202 37.6668 28.3593 37.5264 28.6094 37.2763C28.8594 37.0263 28.9999 36.6871 28.9999 36.3335V33.0002H32.3333C32.6869 33.0002 33.026 32.8597 33.2761 32.6096C33.5261 32.3596 33.6666 32.0205 33.6666 31.6668V30.3335C33.6666 29.9799 33.5261 29.6407 33.2761 29.3907C33.026 29.1406 32.6869 29.0002 32.3333 29.0002H28.9999V25.6668C28.9999 25.3132 28.8594 24.9741 28.6094 24.724C28.3593 24.474 28.0202 24.3335 27.6666 24.3335H26.3333Z"
      />
    ),
  },
  academicMomentum: {
    viewBox: '8.75 24 16.5 12.75',
    paths: (
      <>
        <path {...strokeProps} d="M24.5 28.5V33M9.5 28.5L17 24.75L24.5 28.5L17 32.25L9.5 28.5Z" />
        <path {...strokeProps} d="M12.5 30V33.75C14.75 36 19.25 36 21.5 33.75V30" />
      </>
    ),
  },
};

type ScoreContributionIconProps =
  | { kind: 'wellbeing'; dimension: SaiWellbeingDimension; size?: number }
  | { kind: 'persistence'; dimension: SaiPersistenceDimension; size?: number };

export const ScoreContributionIcon = (
  props: ScoreContributionIconProps
): React.ReactElement | null => {
  const { size = 16 } = props;
  const icon =
    props.kind === 'wellbeing'
      ? WELLBEING_ICONS[props.dimension]
      : PERSISTENCE_ICONS[props.dimension];

  // A dimension a newer API serves has no icon in this bundle. The records are total over the
  // generated unions, and the dimension's name is humanized from the key either way.
  if (!icon) {
    return null;
  }

  return (
    <svg width={size} height={size} viewBox={icon.viewBox} aria-hidden="true" focusable="false">
      {icon.paths}
    </svg>
  );
};
